'use client';

import { useTranslations } from 'next-intl';

import { useAuthContext } from '@/app/[locale]/(legacy)/_providers/auth';
import { asideHeadCallToAction } from '@/app/[locale]/(new)/(public)/projects/[slug]/_page.css';
import { useToast } from '@/app/_components/ui/use-toast';
import { CallToAction } from '@/app/_componentsv2/call-to-action';
import { DetailedProject } from '@/domain/project/types';
import { CampaignStatus } from '@/domain/user/services/types';
import analyticsManager from '@/lib/analytics';
import { transformDetailedProjectToECommerceItem } from '@/lib/analytics/utils';
import Routes from '@/lib/router/routes';
import { useRouter } from '@/lib/translation/hooks';

import { createOrRetrieveInvestmentAndPushToRightInvestSteps } from './action';

interface InvestCtaProps {
  project: Pick<
    DetailedProject,
    'id' | 'name' | 'ticketMini' | 'slug' | 'status'
  > & {
    company: Pick<DetailedProject['company'], 'name'>;
  };
  isInvestor: boolean;
  isLogged: boolean;
}

const InvestCta = ({ project, isInvestor, isLogged }: InvestCtaProps) => {
  const t = useTranslations();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const router = useRouter();

  const isPageLocked = !user;

  const { slug } = project;

  // Projet à l'étude : bouton désactivé, sans compte à rebours (l'ouverture n'est pas datée).
  if (project.status === CampaignStatus.Study) {
    return (
      <CallToAction
        type="button"
        className={asideHeadCallToAction}
        disabled
        label={t('pages.project.blocks.invest.cantInvestYetCta')}
      />
    );
  }

  if (project.status !== CampaignStatus.Visible) {
    return (
      <CallToAction
        className={asideHeadCallToAction}
        type="button"
        disabled
        label={t('pages.project.blocks.invest.closedCta')}
      />
    );
  }

  const ctaText = isInvestor
    ? t('pages.project.blocks.invest.investorCta')
    : t('pages.project.blocks.invest.cta');

  if (!isLogged) {
    return (
      <CallToAction
        className={asideHeadCallToAction}
        label={ctaText}
        type="button"
        onClick={() => router.push(Routes.EspaceAccount)}
        hasArrow={true}
      />
    );
  }

  const handleClick = async () => {
    analyticsManager.client.sendEvent({
      event: 'click_cta',
      link_text: ctaText,
      link_category: 'button',
      link_url: '',
    });
    analyticsManager.client.sendEvent({
      event: 'add_to_cart',
      ecommerce: {
        items: [transformDetailedProjectToECommerceItem(project)],
      },
    });
    try {
      if (project.status !== CampaignStatus.Visible) {
        toast({
          title: t('pages.project.blocks.invest.error.title'),
          description: t(`pages.project.blocks.invest.error.cantInvest`),
          variant: 'destructive',
        });
        return;
      }
      const route = await createOrRetrieveInvestmentAndPushToRightInvestSteps(
        slug,
        user,
      );
      router.push(route);
    } catch (error) {
      const key =
        error instanceof Error ? 'project-not-ready-for-investment' : 'unknown';
      toast({
        title: t('pages.project.blocks.invest.error.title'),
        description: t(`pages.project.blocks.invest.error.${key}`),
        variant: 'destructive',
      });
    }
  };

  return (
    <CallToAction
      className={asideHeadCallToAction}
      type="button"
      onClick={handleClick}
      disabled={isPageLocked}
      label={ctaText}
    />
  );
};

export default InvestCta;
